import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Button,
  calculateSortOrder,
  Card,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  ScrollArea,
  Separator,
  Spinner,
  Text,
  toastManager,
  useIsMobile,
} from "@salestrak/ui";

import { STAGE_TYPES, type StageRow, type StageType } from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { EntityTag } from "#app/components/entity";
import { CreateStageDialog } from "#app/components/stages/create-stage-dialog";
import { DeleteStageDialog } from "#app/components/stages/delete-stage-dialog";
import { useStageMutations } from "#app/lib/hooks/use-stage-mutations";
import { useStagesList } from "#app/lib/hooks/use-stages-list";

import { PageHeader, PageHeaderBack, PageHeaderTitle } from "#app/components/page-header";
import { withPermission } from "#app/components/permissions/require-permission";
import type { Omittable } from "@salestrak/ui";

// Display order is the declaration order of the closed set.
const STAGE_TYPE_LABELS: Record<StageType, string> = {
  pending: "Pending",
  inProgress: "In Progress",
  done: "Done",
  canceled: "Canceled",
};

type StageGroup = {
  type: string;
  label: string;
  stages: StageRow[];
};

type StageActions = {
  onSetDefault: (stageId: string) => void;
  onEdit: (stageId: string) => void;
  onDelete: (stageId: string, name: string, issueCount: number) => void;
};

function WorkflowStagesPage() {
  const { workflowId } = Route.useParams();
  const { push } = useAppNavigate();
  const { reorderStage, setDefaultStage } = useStageMutations();
  const {
    workflowName,
    stages,
    loading,
    error,
    refetch,
    optimisticUpdateSortOrder,
  } = useStagesList(workflowId);

  const groups = useMemo((): StageGroup[] => {
    return STAGE_TYPES.map((type) => ({
      type,
      label: STAGE_TYPE_LABELS[type],
      stages: stages
        .filter((s) => s.type === type)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    }));
  }, [stages]);

  const stageMap = useMemo(() => {
    const map = new Map<string, StageRow>();
    for (const s of stages) map.set(s.stageId, s);
    return map;
  }, [stages]);

  const [createOpen, setCreateOpen] = useState(false);
  const [createType, setCreateType] = useState("inProgress");
  const [createSortOrder, setCreateSortOrder] = useState<number | undefined>();
  const [activeStage, setActiveStage] = useState<StageRow | null>(null);

  const [deleteStageId, setDeleteStageId] = useState<string | null>(null);
  const [deleteStageName, setDeleteStageName] = useState("");
  const [deleteIssueCount, setDeleteIssueCount] = useState(0);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isMobile = useIsMobile();
  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 8 } });
  const sensors = useSensors(...(isMobile ? [] : [pointerSensor]));

  const handleAddStage = useCallback(
    (type: string, existingStages: StageRow[]) => {
      const lastStage = existingStages.at(-1);
      const lastSortOrder = lastStage ? lastStage.sortOrder : 0;
      setCreateType(type);
      setCreateSortOrder(lastSortOrder + 1);
      setCreateOpen(true);
    },
    [],
  );

  const handleEditStage = useCallback(
    (stageId: string) => {
      push(`/settings/workflows/${workflowId}/stages/${stageId}`);
    },
    [push, workflowId],
  );

  const handleSetDefault = useCallback(
    async (stageId: string) => {
      try {
        await setDefaultStage(workflowId, stageId);
        toastManager.add({ title: "Stage set as default", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to set default stage");
      }
    },
    [workflowId, setDefaultStage],
  );

  const handleDeleteStage = useCallback(
    (stageId: string, name: string, issueCount: number) => {
      setDeleteStageId(stageId);
      setDeleteStageName(name);
      setDeleteIssueCount(issueCount);
      setDeleteOpen(true);
    },
    [],
  );

  const handleManageWorkflow = useCallback(() => {
    push(`/settings/workflows/${workflowId}`);
  }, [push, workflowId]);

  const stageActions: StageActions = useMemo(
    () => ({
      onSetDefault: handleSetDefault,
      onEdit: handleEditStage,
      onDelete: handleDeleteStage,
    }),
    [handleSetDefault, handleEditStage, handleDeleteStage],
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const stage = stageMap.get(event.active.id as string) ?? null;
      setActiveStage(stage);
    },
    [stageMap],
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveStage(null);
      const { active, over } = event;
      if (!over) return;

      const draggedStage = stageMap.get(active.id as string);
      if (!draggedStage) return;

      const overStage = stageMap.get(over.id as string);
      if (!overStage || overStage.stageId === draggedStage.stageId) return;

      // Only reorder within the same group
      if (overStage.type !== draggedStage.type) return;

      // Build group stages excluding the dragged one
      const groupStages = groups
        .find((g) => g.type === draggedStage.type)
        ?.stages.filter((s) => s.stageId !== draggedStage.stageId);
      if (!groupStages) return;

      const overIndex = groupStages.findIndex(
        (s) => s.stageId === overStage.stageId,
      );
      if (overIndex < 0) return;

      // Dragging down → place after; dragging up → place before
      const placeAfter = draggedStage.sortOrder < overStage.sortOrder;
      const insertIndex = placeAfter ? overIndex + 1 : overIndex;

      const upperSort = groupStages[insertIndex - 1]?.sortOrder ?? null;
      const lowerSort = groupStages[insertIndex]?.sortOrder ?? null;
      const newSortOrder = calculateSortOrder(upperSort, lowerSort);

      if (draggedStage.sortOrder === newSortOrder) return;

      // Optimistic cache update
      optimisticUpdateSortOrder(draggedStage.stageId, newSortOrder);

      try {
        await reorderStage(draggedStage.stageId, newSortOrder);
      } catch (err) {
        await refetch();
        toastMutationError(err, "Failed to reorder stage");
      }
    },
    [stageMap, groups, reorderStage, refetch, optimisticUpdateSortOrder],
  );

  if (loading && stages.length === 0) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (error && stages.length === 0) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Unable to load stages</EmptyTitle>
            <EmptyDescription>{error.message}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </Box>
    );
  }

  return (
    <Box fill="flex">
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderBack label={workflowName} onClick={() => push(`/settings/workflows/${workflowId}`)} />
          <PageHeaderTitle>Stages</PageHeaderTitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="xl" fill="width" maxWidth={800}>
            <Box gap="xs">
              <Text size="sm" variant="muted">
                Stages define the workflow that a project can go through
              </Text>
            </Box>

            <Box rounded="lg" overflow="hidden">
              <Card>
                {groups.map((group, groupIndex) => (
                  <StageGroupSection
                    key={group.type}
                    group={group}
                    showTopSeparator={groupIndex > 0}
                    onAddStage={handleAddStage}
                    onStageClick={handleEditStage}
                    stageActions={stageActions}
                  />
                ))}
              </Card>
            </Box>
          </Box>

          <CreateStageDialog
            open={createOpen}
            onOpenChange={setCreateOpen}
            workflowId={workflowId}
            stageType={createType}
            sortOrder={createSortOrder}
          />

          <DeleteStageDialog
            stageId={deleteStageId}
            stageName={deleteStageName}
            issueCount={deleteIssueCount}
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            onManageWorkflow={handleManageWorkflow}
          />
        </Box>
      </ScrollArea>

      {!isMobile && (
        <DragOverlay dropAnimation={null}>
          {activeStage ? (
            <StageRowContent stage={activeStage} />
          ) : null}
        </DragOverlay>
      )}
    </DndContext>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Shared stage row inner content (layout-free fragment)
// ---------------------------------------------------------------------------

// The grip lane is always 16px wide so revealing it never shifts the row.
// "hover" fades it in with the row's hover group; the drag overlay asks for
// "always".
type GripMode = "none" | "hover" | "always";

function StageRowInner({
  stage,
  grip = "none",
}: Omittable<{
  stage: StageRow;
  grip?: GripMode;
}>) {
  return (
    <Box direction="row" align="center" gap="none">
      <Box
        width={16}
        align="center"
        justify="center"
        hoverReveal={grip === "hover" ? "sortable-row" : undefined}
      >
        {grip === "none" ? null : (
          <Icon name="grip-vertical" size="md" color="muted" />
        )}
      </Box>
      <Box direction="row" align="center" gap="sm">
        {/* Interactive — click opens StageDetailDialog (stopPropagation keeps
            the row's own click for navigation). */}
        <EntityTag entity={{ kind: "stage", id: stage.stageId, stage }} />
        <Box direction="row" gap="xs" align="center">
          <Text size="sm" variant="muted">
            · {stage.issueCount} {stage.issueCount === 1 ? "project" : "projects"}
          </Text>
          {stage.isDefault ? (
            <Text size="sm" variant="muted">· Default</Text>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Stage actions dropdown
// ---------------------------------------------------------------------------

function StageActionsMenu({
  stage,
  actions,
  onOpenChange,
}: Omittable<{
  stage: StageRow;
  actions: StageActions;
  onOpenChange?: (open: boolean) => void;
}>) {
  return (
    <Menu onOpenChange={onOpenChange}>
      <MenuTrigger
        render={
          <Button aria-label="Stage actions" variant="ghost" size="icon-xs" />
        }
      >
        <Icon name="more-vertical" size="lg" color="muted" />
      </MenuTrigger>
      <MenuPopup align="end">
        <MenuItem
          onClick={() => actions.onSetDefault(stage.stageId)}
          disabled={stage.isDefault}
        >
          <Icon name="star" size="lg" color="muted" />
          Set as default
        </MenuItem>
        <MenuItem onClick={() => actions.onEdit(stage.stageId)}>
          <Icon name="pencil" size="lg" color="muted" />
          Edit
        </MenuItem>
        <MenuSeparator />
        <MenuItem
          variant="destructive"
          onClick={() => actions.onDelete(stage.stageId, stage.name, stage.issueCount)}
          disabled={stage.isDefault}
        >
          <Icon name="trash-2" size="lg" />
          Delete stage
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}

// ---------------------------------------------------------------------------
// Stage group section with per-group SortableContext
// ---------------------------------------------------------------------------

function StageGroupSection({
  group,
  showTopSeparator,
  onAddStage,
  onStageClick,
  stageActions,
}: {
  group: StageGroup;
  showTopSeparator: boolean;
  onAddStage: (type: string, stages: StageRow[]) => void;
  onStageClick: (stageId: string) => void;
  stageActions: StageActions;
}) {
  const stageIds = useMemo(
    () => group.stages.map((s) => s.stageId),
    [group.stages],
  );

  return (
    <Box>
      {showTopSeparator ? <Separator /> : null}
      {/* Box carries no gradient prop, so the group header's wash is an
          authored class. */}
      <Box
        paddingX="md"
        minHeight={62}
        justify="center"
        className="bg-gradient-to-r from-transparent to-foreground/5 dark:to-white/[0.035]"
      >
        <Box direction="row" align="center" justify="between">
          <Text size="xs" weight="medium" variant="muted">
            {group.label}
          </Text>
          <Button
            aria-label={`Add ${group.label} stage`}
            variant="ghost"
            size="icon-xs"
            onClick={() => onAddStage(group.type, group.stages)}
          >
            <Icon name="plus" size="lg" color="muted" />
          </Button>
        </Box>
      </Box>
      <SortableContext items={stageIds} strategy={verticalListSortingStrategy}>
        {group.stages.length === 0 ? (
          <Box surface="card">
            <Separator />
            <Box paddingX="md" minHeight={62} justify="center">
              <Text size="sm" variant="muted">No stages in this category</Text>
            </Box>
          </Box>
        ) : (
          group.stages.map((stage) => (
            <SortableStageRow
              key={stage.stageId}
              stage={stage}
              onClick={() => onStageClick(stage.stageId)}
              stageActions={stageActions}
            />
          ))
        )}
      </SortableContext>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Sortable stage row
// ---------------------------------------------------------------------------

function SortableStageRow({
  stage,
  onClick,
  stageActions,
}: {
  stage: StageRow;
  onClick: () => void;
  stageActions: StageActions;
}) {
  const isMobile = useIsMobile();
  const didDragRef = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    attributes: { role: _role, ...attributes },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({
    id: stage.stageId,
    disabled: isMobile,
  });

  // Track whether a drag actually occurred to prevent navigation after drop
  useEffect(() => {
    if (isDragging) didDragRef.current = true;
  }, [isDragging]);

  return (
    <Box
      ref={setNodeRef}
      // Box carries no motion props — the streamed drag transform and the
      // drag-state fade are inline style.
      style={{
        transform: CSS.Transform.toString(transform) ?? undefined,
        transition: isSorting ? transition : undefined,
        opacity: isDragging ? 0.3 : 1,
      }}
      cursor={isDragging ? "grabbing" : (isMobile ? undefined : "grab")}
      {...(!isMobile ? (attributes as any) : undefined)}
      {...(!isMobile ? (listeners as any) : undefined)}
    >
      <Box
        group="sortable-row" surface="card"
        hoverBackground
      >
        <Separator />
        <Box direction="row" align="center" paddingRight="md" minHeight={62}>
          <Box
            fill="flex"
            shrink
            direction="row"
            align="center"
            justify="between"
            onPointerUp={(e) => {
              if ((e as any).button !== 0) return;
              if (didDragRef.current) {
                didDragRef.current = false;
                return;
              }
              onClick();
            }}
          >
            <StageRowInner stage={stage} grip={isMobile ? "none" : "hover"} />
          </Box>
          {/* `menuOpen` pins the cluster so it cannot vanish under its own
              open popup. */}
          <Box
            hoverReveal={menuOpen ? undefined : "sortable-row"}
            revealOnTouch
          >
            <StageActionsMenu stage={stage} actions={stageActions} onOpenChange={setMenuOpen} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Drag overlay content
// ---------------------------------------------------------------------------

function StageRowContent({ stage }: { stage: StageRow }) {
  return (
    <Card>
      <Box paddingX="md" minHeight={62} direction="row" align="center" justify="between">
        <StageRowInner stage={stage} grip="always" />
      </Box>
    </Card>
  );
}

export const Route = createFileRoute(
  "/(app)/$workspace/settings/workflows/$workflowId/stages/",
)({ component: withPermission("manage.edit_workflows", WorkflowStagesPage) });
